import { createFileRoute } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/shared/ui/badge'
import { Card, CardContent } from '@/shared/ui/card'
import { Input } from '@/shared/ui/input'
import { PageHeader } from '@/widgets/app-shell/page-header'

export const Route = createFileRoute('/_authed/configuracoes/avancado')({
  component: AvancadoPage,
})

/**
 * Flags de tenant (provisionamento, regime de competência, rateio, programado
 * no banco) NÃO têm endpoint no backend — não há o que ler nem persistir. Os
 * cards replicam o print com os controles DESABILITADOS + "Em breve"; o banner
 * de topo (nav-config: coming-soon) explica a causa. Nada é inventado: os
 * toggles nascem neutros (não lemos o estado real), sem prometer um valor.
 */
function AvancadoPage() {
  const { t } = useTranslation(['settings', 'nav', 'common'])

  return (
    <>
      <PageHeader title={t('settings:advanced.title')} />

      <div className="flex max-w-4xl flex-col gap-4 p-6">
        <SettingCard title={t('settings:advanced.provisioning.title')}>
          <p className="text-ink-muted text-sm">
            {t('settings:advanced.provisioning.description')}
          </p>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              defaultValue={3}
              disabled
              aria-label={t('settings:advanced.provisioning.title')}
              className="w-16"
            />
            <span className="text-ink-muted text-sm">
              {t('settings:advanced.provisioning.suffix')}
            </span>
          </div>
        </SettingCard>

        <SettingCard title={t('settings:advanced.accrual.title')}>
          <p className="text-ink-muted text-sm">{t('settings:advanced.accrual.description')}</p>
          <ComingSoonToggle />
        </SettingCard>

        <SettingCard title={t('settings:advanced.apportionment.title')}>
          <p className="text-ink-muted text-sm">
            {t('settings:advanced.apportionment.description1')}
          </p>
          <p className="text-ink-muted text-sm">
            {t('settings:advanced.apportionment.description2')}
          </p>
          <p className="text-ink-muted text-sm">
            {t('settings:advanced.apportionment.description3')}
          </p>
          <span
            className="text-ink-subtle cursor-not-allowed text-sm"
            aria-disabled
            title={t('common:comingSoon.title')}
          >
            {t('settings:advanced.apportionment.learnMore')}
          </span>
          <ComingSoonToggle />
        </SettingCard>

        <SettingCard title={t('settings:advanced.bankScheduled.title')}>
          <p className="text-ink-muted text-sm">
            {t('settings:advanced.bankScheduled.description')}
          </p>
          <ComingSoonToggle />
        </SettingCard>
      </div>
    </>
  )
}

function SettingCard({ title, children }: { title: string; children: ReactNode }) {
  const { t } = useTranslation('common')
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 pt-4">
        <div className="flex items-center gap-2">
          <h2 className="text-ink text-base font-semibold">{title}</h2>
          <Badge tone="soon">{t('comingSoon.badge')}</Badge>
        </div>
        {children}
      </CardContent>
    </Card>
  )
}

/**
 * Switch apenas VISUAL (fiel ao pill do print), desabilitado e sem estado real.
 * O pill é decorativo (`aria-hidden`): o rótulo "Ativar" + o badge "Em breve"
 * carregam o significado para o leitor de tela.
 */
function ComingSoonToggle() {
  const { t } = useTranslation('settings')
  return (
    <span className="inline-flex items-center gap-2">
      <span
        aria-hidden
        className="border-line bg-surface-2 relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border"
      >
        <span className="bg-ink-subtle ml-0.5 size-3.5 rounded-full" />
      </span>
      <span className="text-ink-muted text-sm">{t('advanced.toggle')}</span>
    </span>
  )
}
